import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useAuditLogEntityNames, useAuditLogs, type AuditLogAction, type AuditLogEntry, type AuditLogFilters } from '../api/admin'
import { Badge, Empty, fmtDateTime } from '../components/ui'
import DateInput from '../components/DateInput'

const ACTIONS: AuditLogAction[] = ['Create', 'Update', 'Delete', 'Login', 'TenantSwitch', 'Export', 'Import']
const actionColor: Record<AuditLogAction, string> = { Create: '#008300', Update: '#2a78d6', Delete: '#e34948', Login: '#4a3aa7', TenantSwitch: '#eda100', Export: '#1baf7a', Import: '#eb6834' }

export default function AuditLogPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setParams(n) }
  // URL keeps plain yyyy-MM-dd dates; the inclusive end-of-day suffix is added only when building the query.
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const filters: AuditLogFilters & { page: number } = { page: Number(params.get('page') ?? 1), search: params.get('search') ?? '', entityName: params.get('entityName') ?? '', entityId: params.get('entityId') ?? '', action: (params.get('action') as AuditLogAction | null) ?? '', from, to: to ? `${to}T23:59:59` : '' }
  const logs = useAuditLogs(filters)
  const names = useAuditLogEntityNames()
  const [open, setOpen] = useState<string | null>(null)

  return (
    <div className="stack">
      <h2>{t('auditLog.title')}</h2>
      <p className="muted small">{t('auditLog.hint')}</p>
      <div className="card filters">
        <input placeholder={t('auditLog.searchPlaceholder')} value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <select value={filters.entityName} onChange={(e) => set('entityName', e.target.value)}>
          <option value="">{t('auditLog.allEntities')}</option>
          {names.data?.map((n) => <option key={n} value={n}>{t(`entity.${n}`, n)}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => set('action', e.target.value)}>
          <option value="">{t('auditLog.allActions')}</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{t(`auditAction.${a}`)}</option>)}
        </select>
        <input placeholder={t('auditLog.entityId')} value={filters.entityId} onChange={(e) => set('entityId', e.target.value)} />
        <label>{t('reports.from')}<DateInput value={from} onChange={(v) => set('from', v)} /></label>
        <label>{t('reports.to')}<DateInput value={to} min={from || undefined} onChange={(v) => set('to', v)} /></label>
      </div>
      <div className="card">
        {logs.isLoading ? <p>{t('common.loading')}</p> : !logs.data?.items.length ? <Empty text={t('auditLog.empty')} /> : (
          <div className="table-wrap"><table className="table compact">
            <thead><tr><th>{t('auditLog.time')}</th><th>{t('auditLog.user')}</th><th>{t('auditLog.action')}</th><th>{t('auditLog.entity')}</th><th>{t('auditLog.changes')}</th></tr></thead>
            <tbody>
              {logs.data.items.map((l) => (
                <tr key={l.id} onClick={() => setOpen(open === l.id ? null : l.id)} style={{ cursor: 'pointer' }}>
                  <td className="small" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(l.timestamp)}</td>
                  <td className="small">{l.userEmail ?? <span className="muted">{t('auditLog.system')}</span>}{l.ipAddress ? <div className="muted">{l.ipAddress}</div> : null}</td>
                  <td><Badge color={actionColor[l.action]}>{t(`auditAction.${l.action}`)}</Badge></td>
                  <td className="small">{t(`entity.${l.entityName}`, l.entityName)}<div className="muted" style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{l.entityId}</div></td>
                  <td className="small"><Changes entry={l} expanded={open === l.id} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        {logs.data && logs.data.totalPages > 1 ? (
          <div className="row between" style={{ marginTop: 8 }}>
            <button className="secondary small-btn" disabled={filters.page <= 1} onClick={() => set('page', String(filters.page - 1))}>‹</button>
            <span className="muted small">{t('common.pageOf', { page: logs.data.page, pages: logs.data.totalPages, total: logs.data.totalCount })}</span>
            <button className="secondary small-btn" disabled={filters.page >= logs.data.totalPages} onClick={() => set('page', String(filters.page + 1))}>›</button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** Renders {"field":{"old":..,"new":..}} diffs as a compact list; full snapshots (create/delete) as key: value. */
export function Changes({ entry, expanded }: { entry: AuditLogEntry; expanded: boolean }) {
  const { t } = useTranslation()
  const c = entry.changes
  if (!c || typeof c !== 'object' || Array.isArray(c)) return <span className="muted">{c ? String(c) : '–'}</span>
  const entries = Object.entries(c as Record<string, unknown>)
  const shown = expanded ? entries : entries.slice(0, 3)
  const val = (v: unknown) => (v == null ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v))
  return (
    <div className="stack" style={{ gap: 2 }}>
      {shown.map(([k, v]) => {
        const diff = v && typeof v === 'object' && 'old' in (v as object) && 'new' in (v as object) ? (v as { old: unknown; new: unknown }) : null
        return (
          <div key={k} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <b>{k}</b>
            {diff ? <><span className="muted" style={{ textDecoration: 'line-through' }}>{trunc(val(diff.old), expanded)}</span><span>→</span><span>{trunc(val(diff.new), expanded)}</span></> : <span>{trunc(val(v), expanded)}</span>}
          </div>
        )
      })}
      {!expanded && entries.length > 3 ? <span className="muted">{t('auditLog.more', { count: entries.length - 3 })}</span> : null}
    </div>
  )
}

const trunc = (s: string, full: boolean) => (full || s.length <= 80 ? s : s.slice(0, 80) + '…')
